'use client';

/**
 * DictateStep — the realtime voice-agent interview (story-creation v2, P2).
 *
 * A warm interviewer asks the child about their story by voice; this panel shows
 * a mic control, the live transcript, a "captured so far" summary once the brief
 * lands, and a countdown meter enforcing the per-session cap. On brief (or cap)
 * it maps to ContentParameters and jumps to `output`.
 *
 * On dev (no OpenAI) the hook runs a SCRIPTED fake so the whole flow is demoable.
 * Mic-permission denial and the 402 INSUFFICIENT_TOKENS start response are shown
 * with a message + a fallback hint (switch to type / from-text).
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Progress } from '@/shared/components/ui/progress';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { cn } from '@/shared/utils/cn';
import { Mic, Square, Loader2, Sparkles, AlertTriangle, Check, Plus } from 'lucide-react';
import { Input } from '@/shared/components/ui/input';
import { CAUGHT_FIELDS, useRealtimeStorySession } from '@/contexts/media/realtimeVoice';
import { MicLevelMeter } from '@/contexts/media/components/MicLevelMeter';
import type { StoryBrief, NewCharacter, ExistingCharacter } from '@/contexts/media/realtimeVoice';

/** Heroes chosen during the voice session, handed up alongside the brief. */
export interface DictateHeroesPicked {
  /** Ids of existing library characters to REUSE (→ ContentParameters.characterIds). */
  selectedCharacterIds: string[];
  /** New characters the child invented via the picker's add tool. */
  newCharacters: NewCharacter[];
  /** The family's existing characters, so the wizard can resolve hero NAMES the
   *  agent listed in the brief to library ids (reuse) even when the picker tools
   *  weren't called. */
  existingCharacters: ExistingCharacter[];
}

/**
 * The cap the server granted is what the meter counts down from; this is only
 * the fallback for the first render, before the session answers. A solo brief
 * gets two minutes, composing together seven (sessionCapSeconds).
 */
const CAP_FALLBACK_SECONDS = 120;

interface DictateStepProps {
  locale: string;
  childName?: string;
  /** `together` = several children composing out loud: longer session, own rules. */
  mode?: 'solo' | 'together';
  /** Fired once with the collected brief + the heroes picked during the session. */
  onBrief: (brief: StoryBrief, heroes: DictateHeroesPicked) => void;
}

export function DictateStep({ locale, childName, mode, onBrief }: DictateStepProps) {
  const t = useTranslations('content.wizard.dictate');
  const session = useRealtimeStorySession({ locale, childName, mode });
  const {
    state,
    transcript,
    brief,
    secondsLeft,
    capSeconds,
    caught,
    error,
    existingCharacters,
    selectedCharacterIds,
    newCharacters,
    pickCharacter,
    micLevel,
    start,
    stop,
  } = session;
  const [heroQuery, setHeroQuery] = useState('');

  // Seamless like every premium op — the server enforces the allowance (the
  // session hold is refunded to actual use).
  const requestStart = () => start();

  // Hand the captured brief + picked heroes up exactly once.
  const deliveredRef = useRef(false);
  useEffect(() => {
    if (brief && !deliveredRef.current) {
      deliveredRef.current = true;
      onBrief(brief, { selectedCharacterIds, newCharacters, existingCharacters });
    }
  }, [brief, onBrief, selectedCharacterIds, newCharacters, existingCharacters]);

  // Auto-scroll the transcript to the newest line.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [transcript]);

  const isLive = state === 'listening';
  const isBusy = state === 'starting' || state === 'connecting';
  const mins = Math.floor(Math.max(0, secondsLeft) / 60);
  const secs = Math.max(0, secondsLeft) % 60;
  const timeLabel = `${mins}:${secs.toString().padStart(2, '0')}`;
  const together = mode === 'together';
  const cap = capSeconds || CAP_FALLBACK_SECONDS;
  const pct = secondsLeft > 0 ? Math.min(100, (secondsLeft / cap) * 100) : 0;

  return (
    <div data-testid="wizard-step-dictate" className="space-y-4">
      <div className="space-y-1">
        {/* Composing together is addressed to the room, not to one child. */}
        <h3 className="text-base font-medium">{t(together ? 'togetherTitle' : 'title')}</h3>
        <p className="text-sm text-muted-foreground">{t(together ? 'togetherSubtitle' : 'subtitle')}</p>
      </div>

      {/* Error / fallback notice */}
      {state === 'error' && error && (
        <Alert variant="destructive" data-testid="dictate-error">
          <AlertTriangle className="size-4" />
          <AlertDescription>
            {error.code === 'micDenied'
              ? t('micDenied')
              : error.code === 'insufficientTokens'
                ? t('insufficientTokens')
                : t('error')}
          </AlertDescription>
        </Alert>
      )}

      {/* Countdown meter (visible once live) */}
      {(isLive || state === 'done') && (
        <div className="space-y-1.5" data-testid="dictate-timer">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{t('secondsLeft')}</span>
            <span className="tabular-nums font-medium">{timeLabel}</span>
          </div>
          <Progress value={pct} className="h-2" />
        </div>
      )}

      {/* Mic control */}
      <div className="flex justify-center py-2">
        {isLive ? (
          <Button
            size="lg"
            variant="destructive"
            onClick={stop}
            data-testid="dictate-stop"
            className="gap-2"
          >
            <Square className="size-5" />
            {t('stop')}
          </Button>
        ) : (
          <Button
            size="lg"
            onClick={requestStart}
            disabled={isBusy}
            data-testid="dictate-start"
            className="gap-2"
          >
            {isBusy ? <Loader2 className="size-5 animate-spin" /> : <Mic className="size-5" />}
            {isBusy ? t('listening') : t('start')}
          </Button>
        )}
      </div>
      {/* The agent talks first, so no "no signal" nag here (elapsedSeconds 0) —
          just the live level so the child sees the mic hears them. */}
      {isLive && <MicLevelMeter level={micLevel} className="mx-auto max-w-xs" />}

      {/* What has been caught, while they are still talking.
          Children speaking to a voice with no face cannot tell whether it heard
          them; each settled answer becomes a card, so being heard is visible —
          and the parent sees the story before paying for it. */}
      {Object.keys(caught).length > 0 && (
        <div className="grid grid-cols-2 gap-2" data-testid="dictate-caught">
          {CAUGHT_FIELDS.filter((f) => caught[f]).map((f) => (
            <div
              key={f}
              className="rounded-lg border border-primary/30 bg-primary/5 p-2.5"
              data-testid={`dictate-caught-${f}`}
            >
              <p className="text-[11px] uppercase tracking-wide text-primary/80">{t(`caught.${f}`)}</p>
              <p className="text-sm font-medium">{caught[f]}</p>
            </div>
          ))}
        </div>
      )}

      {/* Heroes picker — the family's existing characters as pick-cards + any new
          ones the child invents mid-session. Hidden once the brief is captured. */}
      {state !== 'done' && (existingCharacters.length > 0 || newCharacters.length > 0) && (
        <div className="space-y-3" data-testid="dictate-heroes">
          {existingCharacters.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium">{t('yourHeroes')}</p>
                {existingCharacters.length > 6 && (
                  <Input
                    value={heroQuery}
                    onChange={(e) => setHeroQuery(e.target.value)}
                    placeholder={t('searchHeroes')}
                    className="h-8 max-w-[10rem] text-sm"
                    data-testid="dictate-heroes-search"
                  />
                )}
              </div>
              <p className="text-xs text-muted-foreground">{t('tapToInclude')}</p>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {existingCharacters
                  .filter((c) =>
                    heroQuery.trim()
                      ? c.name.toLowerCase().includes(heroQuery.trim().toLowerCase())
                      : true,
                  )
                  .map((c) => {
                    const selected = selectedCharacterIds.includes(c.id);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => pickCharacter(c.id)}
                        data-testid={`dictate-hero-${c.id}`}
                        aria-pressed={selected}
                        className={cn(
                          'group relative flex flex-col items-center gap-1 rounded-lg border p-2 text-center transition-colors',
                          selected
                            ? 'border-primary bg-primary/5 ring-1 ring-primary'
                            : 'border-border hover:border-primary/40 hover:bg-muted/40',
                        )}
                      >
                        <div className="relative aspect-square w-full overflow-hidden rounded-md bg-muted">
                          {c.imageUrl ? (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img
                              src={c.imageUrl}
                              alt={c.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-lg font-semibold text-muted-foreground">
                              {c.name.slice(0, 1).toUpperCase()}
                            </div>
                          )}
                          {selected && (
                            <div className="absolute right-1 top-1 rounded-full bg-primary p-0.5 text-primary-foreground">
                              <Check className="size-3" />
                            </div>
                          )}
                        </div>
                        <span className="line-clamp-1 w-full text-xs font-medium">{c.name}</span>
                      </button>
                    );
                  })}
              </div>
            </div>
          )}

          {newCharacters.length > 0 && (
            <div className="space-y-1.5" data-testid="dictate-new-heroes">
              <p className="text-sm font-medium">{t('newHeroes')}</p>
              <div className="flex flex-wrap gap-1.5">
                {newCharacters.map((c) => (
                  <span
                    key={c.name}
                    className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/5 px-2.5 py-1 text-xs font-medium"
                  >
                    <Plus className="size-3 text-primary" />
                    {c.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Live transcript */}
      {transcript.length > 0 && (
        <div
          ref={scrollRef}
          data-testid="dictate-transcript"
          className="max-h-56 space-y-2 overflow-y-auto scrollbar-thin rounded-lg border bg-muted/30 p-3"
        >
          {transcript.map((line) => (
            <div
              key={line.id}
              className={cn('flex', line.role === 'user' ? 'justify-end' : 'justify-start')}
            >
              <span
                className={cn(
                  'inline-block max-w-[85%] rounded-lg px-3 py-1.5 text-sm',
                  line.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background border',
                  !line.final && 'opacity-70',
                )}
              >
                {line.text}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Captured-so-far / brief summary */}
      {brief && (
        <Card data-testid="dictate-brief" className="border-primary/40">
          <CardContent className="space-y-2 p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-primary">
              <Sparkles className="size-4" />
              {t('captured')}
            </div>
            {brief.title && <p className="text-sm font-semibold">{brief.title}</p>}
            <p className="text-sm text-muted-foreground">{brief.synopsis}</p>
            {brief.characters && brief.characters.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {brief.characters.map((c) => c.name).join(', ')}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Done affordance — kept for accessibility; navigation happens on brief. */}
      {isLive && (
        <p className="text-center text-xs text-muted-foreground">{t('done')}</p>
      )}
    </div>
  );
}
